<ng-template [appRouteDialog]="{ size: 'md' }">
  <sb-dialog-header>
    <h3 sb-dialog-title>
      @if (mode === configureMode.EDIT) {
        {{
          action === configureAction.AUTHENTICATE
            ? ('Edit [integrationType] authentication information'
              | translate: { integrationType: integration?.display_name })
            : ('Edit [integrationType] configuration information'
              | translate: { integrationType: integration?.display_name })
        }}
      } @else {
        {{
          action === configureAction.AUTHENTICATE
            ? ('Authenticate with [integrationType]' | translate: { integrationType: integration?.display_name })
            : ('Configure [integrationType] integration' | translate: { integrationType: integration?.display_name })
        }}
      }
    </h3>
  </sb-dialog-header>

  @if (action === configureAction.AUTHENTICATE) {
    <sb-dialog-content>
      <form
        id="app-configure-auth-form"
        (ngSubmit)="authenticate()"
        [formGroup]="authenticateForm"
        sb-dialog-form
        novalidate
      >
        @for (control of appConfig.authForm; track control) {
          <sb-form-field
            [control]="authenticateForm.get(control.name)"
            [label]="control.label | translate"
            [errorWhen]="['touched']"
          >
            <input
              sbInput
              [formControl]="authenticateForm.get(control.name)"
              [type]="control.isPassword ? 'password' : 'text'"
            />
          </sb-form-field>
        }
      </form>
    </sb-dialog-content>
    <sb-dialog-footer>
      <sb-dialog-footer-actions [align]="'end'">
        <button sb-button [color]="'secondary'" type="button" sb-dialog-close>
          {{ 'Cancel' | translate }}
        </button>
        <button sb-button [color]="'primary'" type="submit" form="app-configure-auth-form" [sbLadda]="isLoading">
          {{ 'Connect' | translate }}
        </button>
      </sb-dialog-footer-actions>
    </sb-dialog-footer>
  }

  @if (action === configureAction.CONFIGURE) {
    @if (isLoading && mode === configureMode.EDIT && !integrationConfig) {
      <sb-dialog-content>
        <sb-loading-state />
      </sb-dialog-content>
    } @else {
      <sb-dialog-content>
        <form id="app-configure-form" (ngSubmit)="configure()" [formGroup]="configureForm" sb-dialog-form novalidate>
          @if (mode === configureMode.ADD) {
            <p>
              {{
                'You have successfully made a connection with [integrationType]. The following form will configure the connection.'
                  | translate: { integrationType: integration?.display_name | decodeHtmlString }
              }}
            </p>
          }
          <sb-form-field
            [control]="configureForm.controls['mappingName']"
            [label]="'Integration name' | translate"
            [tooltip]="'This is how the integration will display in lists and reports' | translate"
            [errorWhen]="['touched']"
          >
            <input sbInput [formControl]="configureForm.get('mappingName')" type="text" />
          </sb-form-field>
          @for (control of appConfig.configureForm; track control) {
            <sb-form-field
              [control]="configureForm.get(control.name)"
              [label]="control.label | translate"
              [errorWhen]="['touched']"
            >
              <input sbInput [formControl]="configureForm.get(control.name)" type="text" />
            </sb-form-field>
          }
          @if (appConfig.contractFields?.length) {
            <sb-form-field
              [control]="configureForm.controls['sync_contract']"
              [label]="'Contract fields' | translate"
              [tooltip]="
                'Contract fields that will be managed by [integrationType]'
                  | translate: { integrationType: integration?.display_name | decodeHtmlString }
              "
              [errorWhen]="['touched']"
            >
              <contract-sync
                class="w-full"
                [fields]="appConfig.contractFields"
                [formControl]="configureForm.get('sync_contract')"
              ></contract-sync>
            </sb-form-field>
          }
          @if (appConfig.employeeFields?.length) {
            <sb-form-field
              [control]="configureForm.controls['sync_employee']"
              [label]="'Employee fields' | translate"
              [tooltip]="
                'Employee fields that will be managed by [integrationType]'
                  | translate: { integrationType: integration?.display_name | decodeHtmlString }
              "
              [errorWhen]="['touched']"
            >
              <employee-sync
                class="w-full"
                [fields]="appConfig.employeeFields"
                [formControl]="configureForm.get('sync_employee')"
              ></employee-sync>
            </sb-form-field>
          }
          @if (appConfig.autoAddEmployees) {
            <div class="flex items-center gap-2">
              <sb-checkbox [formControl]="configureForm.get('auto_add_employees')">
                {{ 'Automatically add new employees' | translate }}
              </sb-checkbox>
              <sb-icon
                size="sm"
                name="information-regular"
                [sbTooltip]="'Employees should be automatically added when no match can be found.' | translate"
              />
            </div>
          }
          @if (showPastStartDateSetting) {
            <div class="flex items-center gap-xs">
              <sb-checkbox formControlName="use_past_start_date_on_import_employee">
                {{
                  'Use the contract start date provided by [integrationType]'
                    | translate: { integrationType: integration?.display_name }
                }}
              </sb-checkbox>
              <sb-icon
                size="sm"
                name="information-regular"
                [sbTooltip]="
                  'When enabled, past contract start dates from [integrationType] are synced as-is instead of being replaced with the import date. Absence balances, plus/min hours, and other accrued totals will recalculate from that historical start date. Future-dated contracts are unaffected by this setting.'
                    | translate: { integrationType: integration?.display_name }
                "
              />
            </div>
          }
          @if (appConfig.importVat) {
            <sb-checkbox [formControl]="configureForm.get('importVat')">
              {{ 'Import turnover including VAT' | translate }}
            </sb-checkbox>
          }
          @if (appConfig.notifyImport) {
            <sb-form-field
              [control]="configureForm.controls['notify_import']"
              [label]="'Notify import email address' | translate"
              [errorWhen]="['touched']"
            >
              <input sbInput [formControl]="configureForm.get('notify_import')" type="text" />
            </sb-form-field>
          }
          @if (appConfig.turnOverImportEmail) {
            <sb-form-field
              [control]="configureForm.controls['turnover_import_email']"
              [label]="'Notify import email address' | translate"
              [errorWhen]="['touched']"
            >
              <input sbInput [formControl]="configureForm.get('turnover_import_email')" type="text" />
            </sb-form-field>
          }
          @if (appConfig.notifyExport) {
            <sb-form-field
              [control]="configureForm.controls['notify_export']"
              [label]="'Notify export email address' | translate"
              [errorWhen]="['touched']"
            >
              <input sbInput [formControl]="configureForm.get('notify_export')" type="text" />
            </sb-form-field>
          }
          @if (displayEmployeeDefaultMapping) {
            <sb-form-field
              [label]="'Default employee mapping' | translate"
              [tooltip]="'Configure default settings for employee data mapping' | translate"
            >
              <default-employee-mapping [parentForm]="configureForm"></default-employee-mapping>
            </sb-form-field>
          }
        </form>
      </sb-dialog-content>
      <sb-dialog-footer>
        <sb-dialog-footer-actions [align]="'end'">
          <button sb-button [color]="'secondary'" type="button" sb-dialog-close>
            {{ 'Cancel' | translate }}
          </button>
          @if (mode === configureMode.EDIT && appConfig?.authForm?.length) {
            <button sb-button [color]="'primary'" type="button" (click)="editAuthenticationInformation()">
              {{ 'Reconnect with [integrationType]' | translate: { integrationType: integration?.display_name } }}
            </button>
          }
          <button sb-button [color]="'primary'" type="submit" form="app-configure-form" [sbLadda]="isLoading">
            {{ 'Save' | translate }}
          </button>
        </sb-dialog-footer-actions>
      </sb-dialog-footer>
    }
  }
</ng-template>
